import React, { useContext, useEffect, useReducer, useState, memo } from "react";

import { GlobalContext } from "@/contexts/GlobalContext";
import { ModalContext } from "@/contexts/ModalsContext";

import useFetch from "@/hooks/useFetchData";

import { VehicleIndex } from "@/types/Vehicles.types";

import styles from "@/styles/components.module.scss";

interface Selections {
    make?: string | null | undefined;
    model?: string | null | undefined;
    years?: string | null | undefined;
    engine?: string | null | undefined;
};

interface Options {
    name?: string;
    label?: string;
    value?: string;
};

interface SelectOptions {
    label: string;
    options: Options[];
};

interface ManualState {
    type?: string;
    makes?: string[] | null;
    models?: VehicleIndex[] | null;
};

enum StateTypes {
    SET_MAKES_LIST = "SET_MAKES_LIST",
    SET_MODELS_LIST = "SET_MODELS_LIST"
};

const initialState: ManualState = {
    makes: null,
    models: null
};

type Action = ManualState;

const reducer = (state: ManualState, action: Action) => {
    switch (action.type) {
        case StateTypes.SET_MAKES_LIST:
            return {...state, makes: action.makes};
        case StateTypes.SET_MODELS_LIST:
            return {...state, models: action.models};
        default:
            return state;
    }
};

const ManualSearch = () => {
    const { globalState: { apiUrl } } = useContext(GlobalContext);
    const { modalState: { forms } } = useContext(ModalContext);
    const [state, dispatch] = useReducer(reducer, initialState);
    const { useFetchData } = useFetch();

    const [distModels, setDistModels] = useState<VehicleIndex[] | null>(null);
    const [modelOptions, setModelOptions] = useState<SelectOptions[] | null>(null);
    const [engOptions, setEngOptions] = useState<SelectOptions[] | null>(null);
    const [selected, setSelected] = useState<Selections | null>(null);
    const [ktype, setKtype] = useState<string | null>(null);
    const [vhResults, setVhResults] = useState<VehicleIndex[] | null>(null);

    const [submitForm, setSubmitForm] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const fuelTypes: string[] = ["Petrol", "Diesel", "Hybrid", "Electric", "Wankel", "2-Stroke"];

    useEffect(() => {
        if (state.models && distModels) {
            setModelOptions(selectifyModels());
        }
    }, [state.models, distModels]);

    useEffect(() => {
        if (forms) {
            fetchVehicleMakes();
        }
    }, [forms]);

    useEffect(() => {
        if (selected?.make) {
            fetchVehicleModels();
            setSelected({ ...selected, model: null });
        }
    }, [selected?.make]);

    useEffect(() => {
        if (selected?.model) {
            setEngOptions(selectifyEngines());
        }
    }, [selected?.model]);

    useEffect(() => {
        if (submitForm) {
            filterSelection();
            setSubmitForm(false);
        }
    }, [submitForm]);

    useEffect(() => {
        if (ktype) {
            fetchVehicle(ktype);
        }
    }, [ktype]);

    const fetchVehicleMakes = async () => {
        const url: string = apiUrl + "/vehicles/makes";

        try {
            const result = await useFetchData<string[]>(url);

            if (result.success) {
                dispatch({ type: "SET_MAKES_LIST", makes: result.data });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchVehicleModels = async () => {
        const params = encodeURI(selected?.make!);
        const url: string = `${apiUrl}/vehicles/models?make=${params}&qt=dm`;

        try {
            const result = await useFetchData<{models: VehicleIndex[]; distinct: VehicleIndex[]}>(url);

            if (result.success) {
                dispatch({ type: "SET_MODELS_LIST", models: result.data.models });
                setDistModels(result.data.distinct);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchVehicle = async (ktype: string) => {
		const url: string = `${apiUrl}/vehicles/details?kcode=${ktype}`;

		try {
			const result = await useFetchData<{vehicle: VehicleIndex}>(url);

			if (!result.success) {
                return false;
			}

            const vh = result.data.vehicle;
            const make = vh.MAKE.toLowerCase().replace(/\s+|,\s/g, "-");
            const model = vh.MODEL1.toLowerCase().replace(/\s+|,\s/g, "-");

            localStorage.setItem("__vhi", JSON.stringify(vh));
            localStorage.setItem("__ktype", vh.KCODE_B);
            localStorage.setItem("_lvsd", new Date().getTime().toString());
            localStorage.removeItem("_vrmi");

            window.location.href = `/car-parts/${make}/${model.replace(/\//g, "-")}/${vh.KCODE_B}`;

            setIsFetching(false);
		} catch (error) {
			console.error(error);
		}
	};

    const selectifyModels = (): SelectOptions[] => {
        const reactOptions: SelectOptions[] = [];

        distModels?.forEach(model => {
            let groupLabel: string | null = model.MODEL1;
            let options: Options[] = [];
            state.models?.forEach(({MODEL1, MODEL2, SUBMODEL, BODY}) => {
                if (groupLabel === MODEL1) {
                    options.push({
                        label: `${MODEL1} ${MODEL2 ? "(" + MODEL2 + ")" : ""} ${SUBMODEL ? "[" + SUBMODEL + "]" : ""} - ${BODY}`.replace(/\s{3}|\s{2}/g, " "),
                        value: `${MODEL1} ${MODEL2 ? "(" + MODEL2 + ")" : ""} ${SUBMODEL ? "[" + SUBMODEL + "]" : ""} - ${BODY}`.replace(/\s{3}|\s{2}/g, " ")
                    });
                }
            });

            reactOptions.push({
                label: groupLabel!,
                options: options.filter((el, idx, self) => self.findIndex(t => (t.label === el.label)) === idx)
            });
        });

        return reactOptions;
    };

    const selectifyEngines = () => {
        const reactOptions: SelectOptions[] = [];
        const engines: VehicleIndex[] = [];

        for (let i: number = 0; i < state.models!.length; i++) {
            const vh = state.models![i];
            const model1 = vh.MODEL1;
            const model2 = vh.MODEL2;
            const submodel = vh.SUBMODEL;
            const body = vh.BODY;
            const sel = `${model1} ${model2 ? "(" + model2 + ")" : ""} ${submodel ? "[" + submodel + "]" : ""} - ${body}`.replace(/\s{3}|\s{2}/g, " ");
            if (selected?.model === sel) {
                engines.push(vh);
            }
        }

        fuelTypes.forEach(ft => {
            const options: Options[] = [];
            engines?.forEach(({ FUEL, BHP, KW, ENG1, FROMYR, TOYR }) => {
                if (ft === FUEL) {
                    options.push({
                        label: `${ENG1} - ${BHP} BHP/${KW} KW (${FROMYR}-${TOYR})`.replace(/\s{3}|\s{2}/g, " "),
                        value: `${ENG1} - ${BHP} BHP/${KW} KW (${FROMYR}-${TOYR})`.replace(/\s{3}|\s{2}/g, " ")
                    });
                }
            });

            if (options.length > 0) {
                reactOptions.push({
                    label: ft,
                    options: options.filter((el, idx, self) => self.findIndex(t => (t.label === el.label)) === idx)
                });
            }
        });
        return reactOptions;
    };

    const filterSelection = () => {
        const filtered: VehicleIndex[] = [];

        state.models?.forEach(mdl => {
            const model: string = `${mdl.MODEL1} ${mdl.MODEL2 ? "(" + mdl.MODEL2 + ")" : ""} ${mdl.SUBMODEL ? "[" + mdl.SUBMODEL + "]" : ""} - ${mdl.BODY}`.replace(/\s{3}|\s{2}/g, " ");
            const engine: string = `${mdl.ENG1} - ${mdl.BHP} BHP/${mdl.KW} KW (${mdl.FROMYR}-${mdl.TOYR})`.replace(/\s{3}|\s{2}/g, " ");

            if (`${model} - ${engine}` === `${selected?.model} - ${selected?.engine}`) {
                filtered.push(mdl);
            }
        });

        if (filtered.length === 1) {
            return setKtype(filtered[0].KCODE_B);
        }

        setVhResults(filtered);
    }

    const handleSelectMake = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.currentTarget;

        if (value === "--Make--") {
            return setSelected({ ...selected, make: null, model: null });
        }

        setSelected({ ...selected, make: value });
    };

    const handleSelectModel = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.currentTarget;

        if (value === "--Model--") {
            return setSelected({ ...selected, model: null });
        }

        setSelected({ ...selected, model: value });
    };

    const handleSelectEngine = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.currentTarget;

        if (value === "--Engine & Year--") {
            return setSelected({ ...selected, engine: null });
        }

        setSelected({ ...selected, engine: value });
    };

    const handleSubmitForm = (event: React.FormEvent) => {
        event.preventDefault();

        if (selected?.make && selected.model && selected.engine) {
            setIsFetching(true);
            setSubmitForm(true);
        }
    };

    const handleSelectVehicle = (event: React.MouseEvent<HTMLButtonElement>) => {
        const ktypeNr = event.currentTarget.getAttribute("data-ktype") as string;
        setKtype(ktypeNr);
    };

    const vehicleSelection = (vh: VehicleIndex) => {
        const model = `${vh.MODEL1} ${vh.MODEL2 ? "(" + vh.MODEL2 + ")" : ""}`;
        const subModel = `${vh.SUBMODEL ? "[" + vh.SUBMODEL + "]" : ""}`;
        const body = `${vh.BODY} -`.replace(/\s{3}|\s{2}/g, " ");
        const engine = `${vh.ENG1} ${vh.FUEL} ${vh.CYL && vh.VALVES ? (parseInt(vh.CYL) * parseInt(vh.VALVES)) + "V" : ""} (${vh.ENGCODE1})`;
        const power = `${vh.BHP} BHP/${vh.KW} KW`;
        const drive = `${vh.DRIVE ? "- " + vh.DRIVE : ""}`;
        const dates = `(${vh.FROMYR}-${vh.TOYR})`.replace(/\s{3}|\s{2}/g, " ");

        return `${model} ${subModel} - ${body} ${engine} - ${power} ${drive} ${dates}`;
    };

    return (
        <div className={styles["manual-search"]}>
            <p>Manually select your vehicle below</p>
            <form onSubmit={handleSubmitForm} className={styles["manual-form"]}>
                <select value={selected?.make || ""} className={styles["select-input"]} onChange={handleSelectMake} >
                    <option value="--Make--">--Make--</option>
                    <optgroup label="Popular Makes">
                        <option value="VAUXHALL">VAUXHALL</option>
                        <option value="FORD">FORD</option>
                        <option value="AUDI">AUDI</option>
                        <option value="VW">VW</option>
                        <option value="RENAULT">RENAULT</option>
                        <option value="TOYOTA">TOYOTA</option>
                        <option value="BMW">BMW</option>
                    </optgroup>
                    <optgroup label="All">
                        {state.makes?.map(make => (
                            <option key={make} value={make}>{make}</option>
                        ))}
                    </optgroup>
                </select>

                <select 
                    value={selected?.model || ""} 
                    className={styles["select-input"]} 
                    disabled={selected?.make && state.models ? false : true} 
                    onChange={handleSelectModel} 
                >
                    <option value="--Model--">--Model--</option>
                    {modelOptions?.map(md => (
                        <optgroup key={md.label} label={md.label}>
                            {md.options.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </optgroup>
                    ))}
                </select>

                <select 
                    value={selected?.engine || ""} 
                    className={styles["select-input"]} 
                    onChange={handleSelectEngine} 
                    disabled={selected?.model ? false : true} 
                >
                    <option value="--Engine & Year--">--Engine & Year--</option>
                    {engOptions?.map(eng => (
                        <optgroup key={eng.label} label={eng.label}>
                            {eng.options.map(opt => (
                                <option key={opt.label} value={opt.value}>{opt.label}</option>
                            ))}
                        </optgroup>
                    ))}
                </select>

                <input 
                    disabled={isFetching ? true : false} 
                    className={styles["manual-submit"]} 
                    type="submit" 
                    value="SEARCH" 
                />
            </form>

            {vhResults && vhResults.length > 1 && <div className={styles["search-results"]} >
                    {vhResults.map(vh => (
                        <button 
                            onClick={handleSelectVehicle} 
                            key={vh.KCODE_B} 
                            data-ktype={vh.KCODE_B} 
                            className={styles["search-result"]} 
                        >
                        {vehicleSelection(vh)}
                        </button>
                    ))}
                </div>}
        </div>
    );
};

export default memo(ManualSearch);
